import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { z } from "zod";
import { ApiError } from "@teamblob/api-client";
import { meApi, type MyJoinRequest } from "@teamblob/api-client/modules/me";
import {
  useAuthStore,
  useAuthLoading,
  useIsAuthenticated,
  useCurrentTenant,
} from "../stores/auth-store.js";
import { useBodyClass } from "../hooks/use-body-class.js";
import {
  AuthField,
  AuthErrorBanner,
} from "../components/desktop/auth-primitives.js";
import { Button } from "@teamblob/ui/components";

/**
 * `/welcome` — post-verification onboarding.
 *
 * Reached after a verified user signs in with no active workspace (login routes
 * here when `tenant === null`). Standalone (not inside the DesktopShell).
 *
 * Presents a CHOICE:
 *   1. Create a new workspace (become its owner) → /dashboard.
 *   2. Join an existing workspace by pasting its shareable join code → creates
 *      a pending request the owner must approve; the user waits here.
 *   3. Do neither → the user stays on this screen (the _authenticated guard
 *      keeps workspace-less users out of the CRM automatically).
 *
 * Guards:
 *   - not authenticated → /login
 *   - already has an active workspace → /dashboard (nothing to onboard)
 */
export const Route = createFileRoute("/welcome")({
  component: WelcomePage,
});

type View = "choice" | "create" | "join";

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[\s_]+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "");
}

const workspaceSchema = z.object({
  name: z.string().min(1, "Workspace name is required."),
  slug: z
    .string()
    .min(3, "Workspace URL must be at least 3 characters.")
    .max(50, "Workspace URL must be at most 50 characters.")
    .regex(
      /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/,
      "Only lowercase letters, numbers, and hyphens. Must not start or end with a hyphen.",
    ),
});

type FormData = z.infer<typeof workspaceSchema>;
type FormErrors = Partial<Record<keyof FormData, string>>;

function WelcomePage() {
  useBodyClass("auth-scroll");
  const navigate = useNavigate();
  const checkAuth = useAuthStore((s) => s.checkAuth);
  const createWorkspace = useAuthStore((s) => s.createWorkspace);
  const isLoading = useAuthLoading();
  const isAuthenticated = useIsAuthenticated();
  const tenant = useCurrentTenant();

  const [view, setView] = useState<View>("choice");
  const [pendingRequests, setPendingRequests] = useState<MyJoinRequest[]>([]);

  // Cold load / refresh: confirm the session before deciding what to render.
  useEffect(() => {
    if (!isAuthenticated && isLoading) void checkAuth();
  }, [isAuthenticated, isLoading, checkAuth]);

  // Route away once auth state is known.
  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      void navigate({ to: "/login" });
    } else if (tenant !== null) {
      // Already onboarded — nothing to create here.
      void navigate({ to: "/dashboard" });
    }
  }, [isLoading, isAuthenticated, tenant, navigate]);

  // Load any existing pending join requests so we can show "waiting for approval".
  useEffect(() => {
    if (!isAuthenticated || tenant !== null) return;
    let cancelled = false;
    void meApi
      .listMyJoinRequests()
      .then((rows) => {
        if (!cancelled) setPendingRequests(rows);
      })
      .catch(() => {
        /* non-fatal — the choice screen still works */
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, tenant]);

  // Spinner while we don't yet know the auth state, or while routing away.
  if (isLoading || !isAuthenticated || tenant !== null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--color-canvas)]">
        <div
          role="status"
          aria-label="Loading"
          className="size-8 animate-spin rounded-full border-2 border-[var(--color-border-default)] border-t-[var(--color-accent-amber)]"
        />
      </div>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center px-4 py-10">
      <div className="w-full max-w-md rounded-[var(--radius-lg)] bg-[var(--color-window-face)] p-8 shadow-[var(--shadow-window)]">
        {view === "choice" ? (
          <ChoiceView
            pendingRequests={pendingRequests}
            onCreate={() => setView("create")}
            onJoin={() => setView("join")}
          />
        ) : view === "create" ? (
          <CreateWorkspaceView
            createWorkspace={createWorkspace}
            onBack={() => setView("choice")}
            onCreated={() => void navigate({ to: "/dashboard" })}
          />
        ) : (
          <JoinWorkspaceView
            onBack={() => setView("choice")}
            onRequested={(req) => {
              setPendingRequests((prev) => {
                const others = prev.filter((r) => r.tenantId !== req.tenantId);
                return [...others, req];
              });
              setView("choice");
            }}
          />
        )}
      </div>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Choice view — pick create vs join, and show pending requests
// ---------------------------------------------------------------------------

function ChoiceView({
  pendingRequests,
  onCreate,
  onJoin,
}: {
  pendingRequests: MyJoinRequest[];
  onCreate: () => void;
  onJoin: () => void;
}) {
  return (
    <>
      <h1 className="mb-1 text-[22px] font-semibold text-[var(--color-text-strong)]">
        Welcome to TeamBlob
      </h1>
      <p className="mb-6 text-[13px] text-[var(--color-text-muted)]">
        Your email is verified. Create your own workspace, or join an existing
        one with a code your team shared.
      </p>

      {pendingRequests.length > 0 ? (
        <div className="mb-5 rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-canvas)] p-3">
          <p className="mb-1 text-[12px] font-semibold text-[var(--color-text-strong)]">
            Waiting for approval
          </p>
          <ul className="space-y-1">
            {pendingRequests.map((req) => (
              <li
                key={req.id}
                className="text-[12px] text-[var(--color-text-muted)]"
              >
                {req.tenantName} — pending owner approval
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="space-y-3">
        <button
          type="button"
          onClick={onCreate}
          className="w-full rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-window-face)] p-4 text-left transition-colors hover:border-[var(--color-accent-amber-dark)]"
        >
          <span className="block text-[14px] font-semibold text-[var(--color-text-strong)]">
            Create a workspace
          </span>
          <span className="block text-[12px] text-[var(--color-text-muted)]">
            Start fresh and become the owner. You can invite your team after.
          </span>
        </button>

        <button
          type="button"
          onClick={onJoin}
          className="w-full rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-window-face)] p-4 text-left transition-colors hover:border-[var(--color-accent-amber-dark)]"
        >
          <span className="block text-[14px] font-semibold text-[var(--color-text-strong)]">
            Join an existing workspace
          </span>
          <span className="block text-[12px] text-[var(--color-text-muted)]">
            Paste a join code from a workspace owner to request access.
          </span>
        </button>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Create workspace view (the original form)
// ---------------------------------------------------------------------------

function CreateWorkspaceView({
  createWorkspace,
  onBack,
  onCreated,
}: {
  createWorkspace: (input: { name: string; slug: string }) => Promise<unknown>;
  onBack: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FormErrors>({});

  const handleNameChange = (value: string) => {
    setName(value);
    if (!slugEdited) setSlug(slugify(value));
  };

  const validate = (): boolean => {
    const result = workspaceSchema.safeParse({ name, slug });
    if (result.success) {
      setFieldErrors({});
      return true;
    }
    const errors: FormErrors = {};
    for (const issue of result.error.issues) {
      const key = issue.path[0] as keyof FormData;
      if (key && !errors[key]) errors[key] = issue.message;
    }
    setFieldErrors(errors);
    return false;
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!validate()) return;

    setSubmitting(true);
    try {
      await createWorkspace({ name, slug });
      onCreated();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.statusCode === 409) {
          setFieldErrors((prev) => ({
            ...prev,
            slug: "That workspace URL is already taken.",
          }));
        } else if (err.statusCode === 422 || err.statusCode === 400) {
          setFormError(
            err.details?.[0]?.message ??
              err.message ??
              "Validation failed. Please check your inputs.",
          );
        } else {
          setFormError("Something went wrong. Please try again.");
        }
      } else {
        setFormError("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <BackButton onClick={onBack} />
      <h1 className="mb-1 text-[22px] font-semibold text-[var(--color-text-strong)]">
        Create your workspace
      </h1>
      <p className="mb-6 text-[13px] text-[var(--color-text-muted)]">
        Set up a workspace to get started — you can invite your team afterwards.
      </p>

      {formError !== null ? (
        <div className="mb-4">
          <AuthErrorBanner message={formError} />
        </div>
      ) : null}

      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <AuthField
          label="Workspace name"
          type="text"
          autoComplete="organization"
          required
          placeholder="Acme Inc."
          value={name}
          onChange={(e) => handleNameChange(e.target.value)}
          error={fieldErrors.name}
        />

        <div className="flex flex-col gap-1.5">
          <label className="text-[12px] font-semibold text-[var(--color-text-strong)]">
            Workspace URL
          </label>
          <div className="flex items-center overflow-hidden rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-window-face)] focus-within:border-[var(--color-accent-amber-dark)]">
            <span className="select-none whitespace-nowrap border-r border-[var(--color-border-default)] bg-[var(--color-canvas)] px-2.5 py-2 text-[12px] text-[var(--color-text-muted)]">
              teamblob.com/
            </span>
            <input
              type="text"
              autoComplete="off"
              required
              placeholder="acme-inc"
              value={slug}
              onChange={(e) => {
                setSlugEdited(true);
                setSlug(e.target.value.toLowerCase());
              }}
              aria-invalid={Boolean(fieldErrors.slug)}
              className="flex-1 bg-transparent px-3 py-2 text-[14px] text-[var(--color-text-strong)] outline-none placeholder:text-[var(--color-text-subtle)]"
            />
          </div>
          {fieldErrors.slug ? (
            <p
              role="alert"
              className="text-[11px] text-[var(--color-text-danger)]"
            >
              {fieldErrors.slug}
            </p>
          ) : null}
        </div>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          isLoading={submitting}
          disabled={submitting}
        >
          {submitting ? "Creating workspace…" : "Create workspace"}
        </Button>
      </form>
    </>
  );
}

// ---------------------------------------------------------------------------
// Join workspace view — paste a code, send a request
// ---------------------------------------------------------------------------

function JoinWorkspaceView({
  onBack,
  onRequested,
}: {
  onBack: () => void;
  onRequested: (req: MyJoinRequest) => void;
}) {
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);

    const trimmed = code.trim();
    if (trimmed.length < 3) {
      setFormError("Enter the join code your workspace owner shared.");
      return;
    }

    setSubmitting(true);
    try {
      const req = await meApi.requestToJoin(trimmed);
      onRequested(req);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.statusCode === 404) {
          setFormError("No workspace matches that code. Double-check it.");
        } else if (err.statusCode === 409) {
          setFormError(
            err.message ??
              "You already requested to join, or you're already a member.",
          );
        } else {
          setFormError("Something went wrong. Please try again.");
        }
      } else {
        setFormError("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <BackButton onClick={onBack} />
      <h1 className="mb-1 text-[22px] font-semibold text-[var(--color-text-strong)]">
        Join a workspace
      </h1>
      <p className="mb-6 text-[13px] text-[var(--color-text-muted)]">
        Paste the join code an owner shared with you. They'll approve your
        request before you get access.
      </p>

      {formError !== null ? (
        <div className="mb-4">
          <AuthErrorBanner message={formError} />
        </div>
      ) : null}

      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <AuthField
          label="Join code"
          type="text"
          autoComplete="off"
          required
          placeholder="TB-XXXXXXXX"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
        />

        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          isLoading={submitting}
          disabled={submitting}
        >
          {submitting ? "Sending request…" : "Request to join"}
        </Button>
      </form>
    </>
  );
}

// ---------------------------------------------------------------------------
// Shared: a small back link
// ---------------------------------------------------------------------------

function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mb-4 text-[12px] font-medium text-[var(--color-text-muted)] hover:text-[var(--color-text-strong)]"
    >
      ← Back
    </button>
  );
}
